<script setup>
import { computed, ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import { formatConversationStageLabel } from 'dashboard/constants/conversationStages';

import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  activeUnifiedFilters: { type: Object, required: true },
  agentList: { type: Array, default: () => [] },
  stageOptions: { type: Array, default: () => [] },
  labelOptions: { type: Array, default: () => [] },
  filterCounts: { type: Object, default: () => ({}) },
  hasActiveUnifiedFilters: { type: Boolean, default: false },
  unifiedResultCount: { type: Number, default: 0 },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'filtersModal',
  'unifiedFilterChange',
]);

const { uiSettings, updateUISettings } = useUISettings();

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));
const formattedUnifiedResultCount = computed(() =>
  formatNumber(props.unifiedResultCount)
);
const showUnifiedMenu = ref(false);
const showStageMenu = ref(false);
const showLabelMenu = ref(false);
const headerCount = computed(() =>
  props.hasActiveUnifiedFilters
    ? formattedUnifiedResultCount.value
    : formattedAllCount.value
);

const activeFilterCount = computed(() => {
  const {
    conversationState = 'all',
    period = 'all',
    agentIds = [],
  } = props.activeUnifiedFilters;
  return (
    agentIds.length +
    (conversationState === 'all' ? 0 : 1) +
    (period === 'all' ? 0 : 1)
  );
});

const labels = {
  title: 'Filtrar conversas',
  clear: 'Limpar',
  conversation: 'Conversas',
  period: 'Periodo',
  agent: 'Agente',
  stage: 'Etapas',
  tags: 'Etiquetas',
  filterByTags: 'Filtrar por etiquetas',
  noTags: 'Nenhuma etiqueta encontrada',
};

const filterButtonLabel = computed(() => {
  return activeFilterCount.value ? `Filtros (${activeFilterCount.value})` : '';
});

const activeStageCount = computed(
  () => props.activeUnifiedFilters.stages?.length || 0
);
const activeLabelCount = computed(
  () => props.activeUnifiedFilters.labels?.length || 0
);

const stageButtonLabel = computed(() =>
  activeStageCount.value ? `Etapas (${activeStageCount.value})` : 'Etapas'
);

const stateOptions = [
  { id: 'all', name: 'Todas as conversas' },
  { id: 'answered', name: 'Conversas respondidas' },
  { id: 'unanswered', name: 'Conversas nao respondidas' },
];

const periodOptions = [
  { id: 'all', name: 'Todos os periodos' },
  { id: 'inside_24h', name: 'Dentro da janela de 24h' },
  { id: 'outside_24h', name: 'Fora da janela de 24h' },
];

const getCount = (group, key) => {
  const count = props.filterCounts?.[group]?.[key] || 0;
  return formatNumber(count);
};

const emitFilterChange = patch => {
  emit('unifiedFilterChange', {
    ...props.activeUnifiedFilters,
    ...patch,
  });
};

const setConversationState = state => {
  emitFilterChange({ conversationState: state });
};

const setPeriod = period => {
  emitFilterChange({ period });
};

const toggleValue = (key, value) => {
  const currentValues = props.activeUnifiedFilters[key] || [];
  const nextValues = currentValues.includes(value)
    ? currentValues.filter(item => item !== value)
    : [...currentValues, value];
  emitFilterChange({ [key]: nextValues });
};

const clearPanelFilters = () => {
  emitFilterChange({ conversationState: 'all', period: 'all', agentIds: [] });
};

const clearStageFilters = () => {
  emitFilterChange({ stages: [] });
};

const clearLabelFilters = () => {
  emitFilterChange({ labels: [] });
};

const openStageMenu = () => {
  showLabelMenu.value = false;
  showUnifiedMenu.value = false;
  showStageMenu.value = true;
};

const openLabelMenu = () => {
  showStageMenu.value = false;
  showUnifiedMenu.value = false;
  showLabelMenu.value = true;
};

const openUnifiedMenu = () => {
  showStageMenu.value = false;
  showLabelMenu.value = false;
  showUnifiedMenu.value = true;
};

const stateLabel = optionId => {
  return stateOptions.find(option => option.id === optionId)?.name || optionId;
};

const periodLabel = optionId => {
  return periodOptions.find(option => option.id === optionId)?.name || optionId;
};

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="flex items-start justify-between gap-2 px-3 py-2"
    :class="{
      'border-b border-n-strong':
        hasAppliedFiltersOrActiveFolders && !hasActiveUnifiedFilters,
    }"
  >
    <div class="flex flex-col min-w-0 gap-2">
      <div class="flex items-center min-w-0 gap-1">
        <h1
          class="text-base font-medium text-n-slate-12 shrink-0"
          :title="pageTitle"
        >
          {{ pageTitle }}
        </h1>
        <span
          v-if="!isListLoading"
          class="px-2 py-1 my-0.5 mx-1 rounded-md bg-n-slate-2 text-xxs text-n-slate-11 shrink-0"
          :title="hasActiveUnifiedFilters ? unifiedResultCount : allCount"
        >
          {{ headerCount }} {{ labels.conversation.toLowerCase() }}
        </span>
      </div>
    </div>
    <div class="flex items-center gap-1">
      <div v-if="!hasActiveFolders" class="relative">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-md border border-n-weak bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-3"
          :class="{
            'border-n-blue-7 bg-n-blue-2 text-n-blue-11': activeStageCount,
          }"
          @click="openStageMenu"
        >
          <i class="i-lucide-list-checks text-sm" />
          <span>{{ stageButtonLabel }}</span>
        </button>
        <div
          v-if="showStageMenu"
          v-on-clickaway="() => (showStageMenu = false)"
          class="absolute z-50 right-0 mt-2 w-[16rem] max-h-[22rem] overflow-y-auto rounded-lg border border-n-weak bg-n-background shadow-lg p-2.5 text-sm"
        >
          <div class="flex items-center justify-between mb-2">
            <h2 class="m-0 text-sm font-medium text-n-slate-12">
              {{ labels.stage }}
            </h2>
            <button
              class="text-xs text-n-blue-text hover:underline"
              type="button"
              @click="clearStageFilters"
            >
              {{ labels.clear }}
            </button>
          </div>
          <label
            v-for="stage in stageOptions"
            :key="stage.id"
            class="flex items-center gap-2 py-1.5 text-n-slate-12 cursor-pointer"
          >
            <input
              :checked="activeUnifiedFilters.stages.includes(stage.id)"
              type="checkbox"
              class="m-0"
              @change="toggleValue('stages', stage.id)"
            />
            <span class="h-2 w-2 rounded-full bg-n-blue-9 shrink-0" />
            <span class="min-w-0 truncate">
              {{ formatConversationStageLabel(stage.name) }}
            </span>
            <span class="ml-auto text-xs text-n-slate-10">
              {{ getCount('stages', stage.id) }}
            </span>
          </label>
        </div>
      </div>
      <div v-if="!hasActiveFolders" class="relative">
        <button
          v-tooltip.top="labels.filterByTags"
          type="button"
          class="inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-md border border-n-weak bg-n-alpha-2 px-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-3"
          :class="{
            'border-n-blue-7 bg-n-blue-2 text-n-blue-11': activeLabelCount,
          }"
          :aria-label="labels.filterByTags"
          @click="openLabelMenu"
        >
          <i class="i-lucide-tags text-sm" />
          <span v-if="activeLabelCount">{{ activeLabelCount }}</span>
        </button>
        <div
          v-if="showLabelMenu"
          v-on-clickaway="() => (showLabelMenu = false)"
          class="absolute z-50 right-0 mt-2 w-[17rem] max-h-[24rem] overflow-y-auto rounded-lg border border-n-weak bg-n-background shadow-lg p-2.5 text-sm"
        >
          <div class="flex items-center justify-between mb-2">
            <h2 class="m-0 text-sm font-medium text-n-slate-12">
              {{ labels.tags }}
            </h2>
            <button
              class="text-xs text-n-blue-text hover:underline"
              type="button"
              @click="clearLabelFilters"
            >
              {{ labels.clear }}
            </button>
          </div>
          <p
            v-if="!labelOptions.length"
            class="m-0 py-2 text-xs text-n-slate-10"
          >
            {{ labels.noTags }}
          </p>
          <label
            v-for="label in labelOptions"
            :key="label.id"
            class="flex items-center gap-2 py-1.5 text-n-slate-12 cursor-pointer"
          >
            <input
              :checked="activeUnifiedFilters.labels?.includes(label.title)"
              type="checkbox"
              class="m-0"
              @change="toggleValue('labels', label.title)"
            />
            <span
              class="h-2.5 w-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            <span class="min-w-0 truncate">{{ label.title }}</span>
            <span class="ml-auto text-xs text-n-slate-10">
              {{ getCount('labels', label.title) }}
            </span>
          </label>
        </div>
      </div>
      <template
        v-if="
          hasAppliedFilters && !hasActiveFolders && !hasActiveUnifiedFilters
        "
      >
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <div v-else class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="'Filtrar conversas'"
          icon="i-lucide-list-filter"
          :label="isOnExpandedLayout ? filterButtonLabel : ''"
          slate
          xs
          faded
          @click="openUnifiedMenu"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
        <div
          v-if="showUnifiedMenu"
          v-on-clickaway="() => (showUnifiedMenu = false)"
          class="absolute z-50 right-0 mt-2 w-[18rem] max-h-[24rem] overflow-y-auto rounded-lg border border-n-weak bg-n-background shadow-lg p-2.5 text-sm"
        >
          <div class="flex items-center justify-between mb-2">
            <h2 class="m-0 text-sm font-medium text-n-slate-12">
              {{ labels.title }}
            </h2>
            <button
              class="text-xs text-n-blue-text hover:underline"
              type="button"
              @click="clearPanelFilters"
            >
              {{ labels.clear }}
            </button>
          </div>
          <div class="space-y-3">
            <div>
              <p class="mb-1 text-xs font-medium text-n-slate-11">
                {{ labels.conversation }}
              </p>
              <label
                v-for="option in stateOptions"
                :key="option.id"
                class="flex items-center gap-2 py-1 text-n-slate-12 cursor-pointer"
              >
                <input
                  :checked="
                    activeUnifiedFilters.conversationState === option.id
                  "
                  type="radio"
                  name="conversation-state-filter"
                  class="m-0"
                  @change="setConversationState(option.id)"
                />
                <span class="min-w-0 truncate">
                  {{ stateLabel(option.id) }}
                </span>
                <span class="ml-auto text-xs text-n-slate-10">
                  {{ getCount('state', option.id) }}
                </span>
              </label>
            </div>
            <div>
              <p class="mb-1 text-xs font-medium text-n-slate-11">
                {{ labels.period }}
              </p>
              <label
                v-for="option in periodOptions"
                :key="option.id"
                class="flex items-center gap-2 py-1 text-n-slate-12 cursor-pointer"
              >
                <input
                  :checked="activeUnifiedFilters.period === option.id"
                  type="radio"
                  name="conversation-period-filter"
                  class="m-0"
                  @change="setPeriod(option.id)"
                />
                <span class="min-w-0 truncate">{{
                  periodLabel(option.id)
                }}</span>
                <span class="ml-auto text-xs text-n-slate-10">
                  {{ getCount('period', option.id) }}
                </span>
              </label>
            </div>
            <div>
              <p class="mb-1 text-xs font-medium text-n-slate-11">
                {{ labels.agent }}
              </p>
              <div class="max-h-28 overflow-auto pr-1">
                <label
                  v-for="agent in agentList"
                  :key="agent.id"
                  class="flex items-center gap-2 py-1 text-n-slate-12 cursor-pointer"
                >
                  <input
                    :checked="activeUnifiedFilters.agentIds.includes(agent.id)"
                    type="checkbox"
                    class="m-0"
                    @change="toggleValue('agentIds', agent.id)"
                  />
                  <span class="min-w-0 truncate">{{ agent.name }}</span>
                  <span class="ml-auto text-xs text-n-slate-10">
                    {{ getCount('agents', agent.id) }}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SwitchLayout
        :is-on-expanded-layout="isOnExpandedLayout"
        @toggle="toggleConversationLayout"
      />
    </div>
  </div>
</template>
